// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, limit, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';

interface OnboardRecord {
  id: string;
  username: string;
  instagramUrl: string;
  status: 'pending' | 'fetching' | 'ready' | 'importing' | 'done' | 'error';
  createdAt: Timestamp;
  postCount?: number;
  shopId?: string;
  error?: string;
}

interface Props {
  isScraperConnected: boolean;
  hasCurrentCommand: boolean;
  onProcessOnboard: (record: OnboardRecord) => void;
}

const timeAgo = (date: any): string => {
  if (!date) return '';
  const now = Date.now();
  const ts = typeof date?.toMillis === 'function' ? date.toMillis() : new Date(date).getTime();
  if (isNaN(ts)) return '';
  const secs = Math.floor((now - ts) / 1000);
  if (secs < 10) return 'now';
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

// ── Status dot with animation per state ──────────────────────────────────

const StatusDot: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'fetching') {
    return (
      <div className="relative w-3 h-3 shrink-0">
        <div className="absolute inset-0 rounded-full bg-blue-500" />
        <div className="absolute inset-0 rounded-full bg-blue-400 animate-ping" />
      </div>
    );
  }
  if (status === 'importing') {
    return (
      <div className="relative w-3 h-3 shrink-0">
        <div className="absolute inset-0 rounded-full bg-purple-500" />
        <div className="absolute inset-0 rounded-full bg-purple-400 animate-ping" />
      </div>
    );
  }
  const colors: Record<string, string> = {
    pending: 'bg-amber-400',
    ready: 'bg-green-500',
    done: 'bg-gray-300',
    error: 'bg-red-500',
  };
  return <div className={`w-3 h-3 rounded-full shrink-0 ${colors[status] || 'bg-gray-300'}`} />;
};

// ── Status label badge ───────────────────────────────────────────────────

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg: Record<string, { bg: string; text: string; label: string }> = {
    pending: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Queued' },
    fetching: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Fetching' },
    ready: { bg: 'bg-green-100', text: 'text-green-700', label: 'Ready' },
    importing: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Importing' },
    done: { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Done' },
    error: { bg: 'bg-red-100', text: 'text-red-700', label: 'Error' },
  };
  const c = cfg[status] || cfg.pending;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// ── Indeterminate progress bar ───────────────────────────────────────────

const IndeterminateBar: React.FC<{ color: string }> = ({ color }) => (
  <div className="mt-1.5 h-0.5 rounded-full overflow-hidden" style={{ backgroundColor: `${color}22` }}>
    <div
      className="h-full rounded-full"
      style={{
        backgroundColor: color,
        width: '40%',
        animation: 'onboard-progress 1.8s ease-in-out infinite',
      }}
    />
  </div>
);

// ── Main OnboardQueue ────────────────────────────────────────────────────

const OnboardQueue: React.FC<Props> = ({ isScraperConnected, hasCurrentCommand, onProcessOnboard }) => {
  const [records, setRecords] = useState<OnboardRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const autoProcessedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const q = query(
      collection(db, 'onboardQueue'),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    const unsub = onSnapshot(q, snap => {
      const items: OnboardRecord[] = snap.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as OnboardRecord));
      setRecords(items);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Auto-process pending records when scraper is idle
  useEffect(() => {
    if (!isScraperConnected || hasCurrentCommand) return;
    const pendingRecord = records.find(
      r => r.status === 'pending' && !autoProcessedRef.current.has(r.id)
    );
    if (pendingRecord) {
      autoProcessedRef.current.add(pendingRecord.id);
      onProcessOnboard(pendingRecord);
    }
  }, [records, isScraperConnected, hasCurrentCommand, onProcessOnboard]);

  const activeRecords = records.filter(r => !['done'].includes(r.status));
  const doneRecords = records.filter(r => r.status === 'done').slice(0, 5);
  const fetchingCount = records.filter(r => r.status === 'fetching').length;

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100">
          <div className="h-4 bg-orange-200/50 rounded w-28 animate-pulse" />
        </div>
        <div className="p-5 space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-3 h-3 rounded-full bg-gray-200" />
              <div className="flex-1 space-y-1.5">
                <div className="h-3 bg-gray-200 rounded w-24" />
                <div className="h-2 bg-gray-100 rounded w-16" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (records.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-orange-200 shadow-sm overflow-hidden">
      {/* CSS for indeterminate bar */}
      <style>{`
        @keyframes onboard-progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(100%); }
          100% { transform: translateX(250%); }
        }
      `}</style>

      {/* Header */}
      <div className="px-5 py-3 bg-gradient-to-r from-orange-50 to-amber-50 border-b border-orange-100 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {fetchingCount > 0 ? (
            <svg className="w-3.5 h-3.5 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          ) : (
            <div className="w-2 h-2 rounded-full bg-orange-400" />
          )}
          <h3 className="text-sm font-bold text-gray-900">Onboard Queue</h3>
          {activeRecords.length > 0 && (
            <span className="px-2 py-0.5 bg-orange-200 text-orange-800 rounded-full text-[10px] font-bold">
              {activeRecords.length}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {fetchingCount > 0 && (
            <span className="text-[10px] text-blue-600 font-bold">Fetching {fetchingCount}...</span>
          )}
          <span className="text-[10px] text-gray-400">Auto</span>
        </div>
      </div>

      {/* Records */}
      <div className="divide-y divide-gray-50">
        {activeRecords.map(rec => (
          <div key={rec.id} className="px-5 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3 min-w-0">
                <StatusDot status={rec.status} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">@{rec.username}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <StatusBadge status={rec.status} />
                    {rec.postCount > 0 && (
                      <span className="text-[10px] text-gray-400 font-medium">{rec.postCount} posts</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] text-gray-400">{timeAgo(rec.createdAt)}</span>
                {rec.status === 'pending' && isScraperConnected && !hasCurrentCommand && (
                  <button
                    onClick={() => onProcessOnboard(rec)}
                    className="px-3 py-1.5 bg-orange-500 text-white text-[10px] font-bold rounded-lg hover:bg-orange-600 active:scale-95 transition-all"
                  >
                    Fetch
                  </button>
                )}
                {rec.status === 'ready' && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-[10px] font-bold rounded-lg border border-green-200">
                    ✓ Mobile
                  </span>
                )}
              </div>
            </div>
            {/* Progress bar for fetching/importing */}
            {rec.status === 'fetching' && <IndeterminateBar color="#3B82F6" />}
            {rec.status === 'importing' && <IndeterminateBar color="#8B5CF6" />}
            {/* Error message */}
            {rec.status === 'error' && rec.error && (
              <p className="mt-1.5 text-[10px] text-red-500 truncate">{rec.error}</p>
            )}
          </div>
        ))}

        {/* Done section */}
        {doneRecords.length > 0 && (
          <>
            <div className="px-5 py-2 bg-gray-50/80">
              <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider">Completed</span>
            </div>
            {doneRecords.map(rec => (
              <div key={rec.id} className="px-5 py-2 flex items-center justify-between opacity-50">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 rounded-full bg-gray-200 shrink-0" />
                  <span className="text-xs text-gray-500">@{rec.username}</span>
                </div>
                <div className="flex items-center gap-2">
                  {rec.shopId && (
                    <span className="text-[10px] text-gray-400 font-mono">{rec.shopId.slice(0, 8)}…</span>
                  )}
                  <span className="text-[10px] text-gray-400">{timeAgo(rec.createdAt)}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default OnboardQueue;
